/**
 * Revert With Draft Modal Component
 *
 * Shows options when reverting to a message with draft pre-population:
 * 1. Keep current model - Continue with your currently selected model
 * 2. Switch to original model - Use the model that was active when this message was sent
 *
 * After selection, reverts to the selected message and pre-populates the message composer
 * with the original AI response content as a draft.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { AIModel } from "@/lib/apiKeyStorage";
import { AVAILABLE_MODELS } from "@/lib/apiKeyStorage";
type RevertWithDraftModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRevertWithCurrentModel: () => void;
  onRevertWithOriginalModel: () => void;
  currentModel: AIModel;
  originalModel: string | null;
  messagesCount: number;
};
const RevertWithDraftModal: React.FC<RevertWithDraftModalProps> = t0 => {
  const $ = _c(46);
  const {
    isOpen,
    onClose,
    onRevertWithCurrentModel,
    onRevertWithOriginalModel,
    currentModel,
    originalModel,
    messagesCount
  } = t0;
  if (!isOpen) {
    return null;
  }
  let t1;
  if ($[0] !== currentModel) {
    t1 = AVAILABLE_MODELS.find(m => m.value === currentModel);
    $[0] = currentModel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentModelInfo = t1;
  let t2;
  if ($[2] !== originalModel) {
    t2 = originalModel ? AVAILABLE_MODELS.find(m_0 => m_0.value === originalModel) : null;
    $[2] = originalModel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const originalModelInfo = t2;
  let t3;
  if ($[4] !== onClose) {
    t3 = <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />;
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h2 className="text-lg font-semibold text-slate-100">Revert with Draft</h2>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClose) {
    t6 = <div className="flex items-center justify-between">{t4}<button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200">{t5}</button></div>;
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = messagesCount !== 1 ? "s" : "";
  let t8;
  if ($[10] !== messagesCount || $[11] !== t7) {
    t8 = <p className="mt-2 text-sm text-slate-400">This will delete {messagesCount} message{t7} after this point and pre-populate the message composer with the original AI response.</p>;
    $[10] = messagesCount;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <div className="border-b border-slate-700 px-6 py-4">{t6}{t8}</div>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-sm text-slate-300">Choose which model to use for the draft:</p>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== onClose || $[18] !== onRevertWithCurrentModel) {
    t11 = () => {
      onRevertWithCurrentModel();
      onClose();
    };
    $[17] = onClose;
    $[18] = onRevertWithCurrentModel;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/20"><svg className="h-3 w-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mb-1 font-medium text-slate-100">Use Current Model</div>;
    t14 = <div className="text-xs text-slate-400 mb-2">Draft with your currently selected model</div>;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  let t15;
  if ($[23] !== currentModelInfo) {
    t15 = currentModelInfo && <div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[10px] ${currentModelInfo.provider === "openai" ? "bg-blue-500/20 text-blue-300" : "bg-orange-500/20 text-orange-300"}`}>{currentModelInfo.provider === "openai" ? "OpenAI" : "Claude"}</span><span className="text-xs font-medium text-slate-300">{currentModelInfo.label}</span></div>;
    $[23] = currentModelInfo;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15) {
    t16 = <div className="flex items-start gap-3">{t12}<div className="flex-1">{t13}{t14}{t15}</div></div>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t11 || $[28] !== t16) {
    t17 = <button onClick={t11} className="w-full rounded-lg border border-blue-500/30 bg-blue-600/10 px-4 py-4 text-left transition-all hover:bg-blue-600/20 hover:border-blue-500/50">{t16}</button>;
    $[27] = t11;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== onClose || $[31] !== onRevertWithOriginalModel || $[32] !== originalModelInfo) {
    t18 = originalModelInfo ? <button onClick={() => {
      onRevertWithOriginalModel();
      onClose();
    }} className="w-full rounded-lg border border-purple-500/30 bg-purple-600/10 px-4 py-4 text-left transition-all hover:bg-purple-600/20 hover:border-purple-500/50"><div className="flex items-start gap-3"><div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-purple-500/20"><svg className="h-3 w-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg></div><div className="flex-1"><div className="mb-1 font-medium text-slate-100">Use Original Model</div><div className="text-xs text-slate-400 mb-2">Draft with the model that was active at this message</div><div className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[10px] ${originalModelInfo.provider === "openai" ? "bg-blue-500/20 text-blue-300" : "bg-orange-500/20 text-orange-300"}`}>{originalModelInfo.provider === "openai" ? "OpenAI" : "Claude"}</span><span className="text-xs font-medium text-slate-300">{originalModelInfo.label}</span></div></div></div></button> : <div className="rounded-lg border border-slate-700 bg-slate-800/50 px-4 py-4"><div className="flex items-start gap-3"><div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-slate-700"><svg className="h-3 w-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div><div className="flex-1"><div className="mb-1 text-sm text-slate-400">Original model information not available</div><div className="text-xs text-slate-500">This message was sent before model tracking was enabled</div></div></div></div>;
    $[30] = onClose;
    $[31] = onRevertWithOriginalModel;
    $[32] = originalModelInfo;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t17 || $[35] !== t18) {
    t19 = <div className="px-6 py-5 space-y-4">{t10}{t17}{t18}</div>;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== onClose) {
    t20 = <div className="border-t border-slate-700 px-6 py-4"><button onClick={onClose} className="w-full rounded-md border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors">Cancel</button></div>;
    $[37] = onClose;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t19 || $[40] !== t20 || $[41] !== t9) {
    t21 = <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"><div className="rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">{t9}{t19}{t20}</div></div>;
    $[39] = t19;
    $[40] = t20;
    $[41] = t9;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t21 || $[44] !== t3) {
    t22 = <>{t3}{t21}</>;
    $[43] = t21;
    $[44] = t3;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
};
export default RevertWithDraftModal;